/**
 * MatchingPanel — replaces DocumentPanel on the right side of WorkspacePage.
 *
 * Subscribes to conversation messages + voice status, fetches a live ranking,
 * and renders the hero TCG card + two challenger cards.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useMatching } from '../../hooks/useMatching';
import { useAppToken } from '../../hooks/useAppToken';
import HeroCard from './HeroCard';
import ChallengerCard from './ChallengerCard';
import DetacheringDossier from '../DetacheringDossier';
import './matching.css';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function MatchingPanel({ voiceStatus, onHeroChange, className = '' }) {
  const { state } = useWorkspace();
  const messages = state.conversation.messages;

  const { ranking, loading, latencyMs, error } = useMatching(messages, voiceStatus);

  // Engineer profile cache, fetched once on mount.
  const { getToken } = useAppToken();
  const [engineersById, setEngineersById] = useState({});
  useEffect(() => {
    (async () => {
      try {
        let token = null;
        try { token = await getToken(); } catch { /* no auth in demo */ }
        const headers = {};
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch(`${API_URL}/api/matching/engineers`, { headers });
        if (!res.ok) return;
        const data = await res.json();
        const map = {};
        (data.engineers || []).forEach(e => { map[e.id] = e; });
        setEngineersById(map);
      } catch (e) {
        console.error('[MatchingPanel] failed to load engineers', e);
      }
    })();
  }, [getToken]);

  const ordered = useMemo(() => {
    return ranking
      .map(r => ({ ...r, engineer: engineersById[r.engineerId] }))
      .filter(r => r.engineer);
  }, [ranking, engineersById]);

  const hero = ordered[0];
  const challengers = ordered.slice(1, 3);

  // Geef de live top-match door naar boven (WorkspacePage injecteert 'm in de
  // voice-agent, zodat die dezelfde naam kan noemen als op het scherm staat).
  useEffect(() => {
    if (!onHeroChange || !hero?.engineer) return;
    onHeroChange({ name: hero.engineer.name, role: hero.engineer.role, score: hero.score });
  }, [hero?.engineer?.id, hero?.score, onHeroChange]);

  const userTurnCount = messages.filter(m => m && m.sender === 'user').length;

  // Volledig dossier van de huidige hero-match, geopend vanuit de HeroCard.
  const [dossierEngineer, setDossierEngineer] = useState(null);

  return (
    <aside className={`matching-panel ${loading ? 'matching-panel--loading' : ''} ${className}`}>
      <div className="matching-panel__header">
        <span className="dot" />
        <span>HUMANAIZE MATCHER · LIVE</span>
        {latencyMs != null && (
          <span className="latency">{Math.round(latencyMs)} ms</span>
        )}
      </div>

      <div className="matching-panel__body">
        <HeroCard
          engineer={hero?.engineer}
          score={hero?.score}
          whyMatch={hero?.whyMatch}
          turnKey={userTurnCount}
          onOpenDossier={hero?.engineer ? () => setDossierEngineer(hero.engineer) : undefined}
        />

        <div className="challenger-row">
          {[0, 1].map(i => {
            const c = challengers[i];
            return (
              <ChallengerCard
                key={i}
                engineer={c?.engineer}
                score={c?.score}
                onClick={() => {
                  /* Swap-animatie komt later; voor nu kort visueel verspringen via state. */
                }}
              />
            );
          })}
        </div>

        {error && (
          <div style={{
            fontSize: 11,
            color: '#b91c1c',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            padding: '6px 10px',
            borderRadius: 4,
            fontFamily: 'var(--hai-mono)'
          }}>
            match-fout: {error}
          </div>
        )}
      </div>

      {dossierEngineer && (
        <DetacheringDossier
          engineer={dossierEngineer}
          onClose={() => setDossierEngineer(null)}
        />
      )}
    </aside>
  );
}
